/*
  assets/kw-split-reveal.js
  <kw-split-reveal> breaks the element marked [data-split-lines] into the lines it actually wraps to,
  puts each line in a mask, and lets the lines slide up one after the other the first time the block
  scrolls into view; with data-split="words" every word rises on its own instead. Elements marked
  [data-reveal-item] fade up around them (before the text if they sit above it, after the last line
  if they sit below). Styles: assets/kw-split-reveal.css.
  <kw-parallax> lets its [data-parallax="<px>"] children drift by up to that many pixels as the block
  travels through the window (negative drifts the other way).
  No library. Without JavaScript, or with reduced motion, everything is simply there and still.
*/
if (!customElements.get('kw-split-reveal')) {
  customElements.define(
    'kw-split-reveal',
    class KwSplitReveal extends HTMLElement {
      connectedCallback() {
        this.heading = this.querySelector('[data-split-lines]');
        this.items = Array.from(this.querySelectorAll('[data-reveal-item]'));
        this.lineCount = 0;
        if (this.heading) {
          this.segments = this.readSegments();
          /* Screen readers get the sentence once, not line by line */
          this.heading.setAttribute('aria-label', this.segments.join(' '));
        }

        const start = () => {
          if (this.started || !this.isConnected) return;
          this.started = true;
          this.split();
          this.setItemDelays();
          this.classList.add('is-split');
          this.observe();
        };
        /* Line breaks depend on the font, so wait for it (but never for long) */
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(start);
        this.startTimer = setTimeout(start, 1500);

        this.lastWidth = window.innerWidth;
        this.onResize = () => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => {
            if (!this.started || window.innerWidth === this.lastWidth) return;
            this.lastWidth = window.innerWidth;
            this.split();
            this.setItemDelays();
          }, 150);
        };
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        clearTimeout(this.startTimer);
        clearTimeout(this.resizeTimer);
        clearTimeout(this.settleTimer);
        if (this.observer) this.observer.disconnect();
      }

      /* The sentence as typed: one entry per typed line (a <br> in the markup). */
      readSegments() {
        const segments = [''];
        this.heading.childNodes.forEach((node) => {
          if (node.nodeName === 'BR') segments.push('');
          else segments[segments.length - 1] += node.textContent;
        });
        return segments.map((segment) => segment.replace(/\s+/g, ' ').trim()).filter(Boolean);
      }

      split() {
        const heading = this.heading;
        if (!heading) return;

        /* 1. Lay the words out loose and see where the browser wraps them */
        heading.textContent = '';
        const words = [];
        this.segments.forEach((segment, index) => {
          if (index > 0) heading.appendChild(document.createElement('br'));
          segment.split(' ').forEach((word) => {
            const span = document.createElement('span');
            span.style.display = 'inline-block';
            span.textContent = word;
            heading.appendChild(span);
            heading.appendChild(document.createTextNode(' '));
            words.push(span);
          });
        });

        const lines = [];
        let lineTop = null;
        words.forEach((span) => {
          const top = span.offsetTop;
          if (lineTop === null || Math.abs(top - lineTop) > 2) {
            lines.push([]);
            lineTop = top;
          }
          lines[lines.length - 1].push(span.textContent);
        });

        /* 2. Rebuild as one masked block per line (and, in words mode, one riser per word) */
        const byWord = this.dataset.split === 'words';
        let wordIndex = 0;
        heading.textContent = '';
        lines.forEach((lineWords, index) => {
          const mask = document.createElement('span');
          mask.className = 'kw-line-mask';
          mask.setAttribute('aria-hidden', 'true');
          const line = document.createElement('span');
          line.className = 'kw-line';
          line.style.setProperty('--kw-line', index);
          if (byWord) {
            lineWords.forEach((text, position) => {
              if (position > 0) line.appendChild(document.createTextNode(' '));
              const word = document.createElement('span');
              word.className = 'kw-word';
              word.style.setProperty('--kw-word', wordIndex++);
              word.textContent = text;
              line.appendChild(word);
            });
          } else {
            line.textContent = lineWords.join(' ');
          }
          mask.appendChild(line);
          heading.appendChild(mask);
        });
        this.lineCount = lines.length;
      }

      setItemDelays() {
        const afterLines = 0.2 + this.lineCount * 0.13 + 0.15;
        this.items.forEach((item) => {
          const above = this.heading && item.compareDocumentPosition(this.heading) & Node.DOCUMENT_POSITION_FOLLOWING;
          item.style.setProperty('--kw-item-delay', `${above || !this.heading ? 0 : afterLines}s`);
        });
      }

      observe() {
        const reveal = () => {
          this.classList.add('is-revealed');
          /* Once the entrance has played, later re-measures must not animate */
          this.settleTimer = setTimeout(() => this.classList.add('is-settled'), 2600);
        };
        if (!('IntersectionObserver' in window)) {
          reveal();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            reveal();
          },
          { threshold: 0.6, rootMargin: '0px 0px -12% 0px' }
        );
        /* Watch the sentence itself, not the padded block around it, so the entrance plays where it can be seen */
        this.observer.observe(this.heading || this);
      }
    }
  );
}

if (!customElements.get('kw-parallax')) {
  customElements.define(
    'kw-parallax',
    class KwParallax extends HTMLElement {
      connectedCallback() {
        this.layers = Array.from(this.querySelectorAll('[data-parallax]'));
        const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!this.layers.length || still || !('IntersectionObserver' in window)) return;

        this.update = this.update.bind(this);
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(this.update);
        };
        /* Only listen while the block is near the window */
        this.observer = new IntersectionObserver(
          (entries) => {
            const near = entries.some((entry) => entry.isIntersecting);
            window[near ? 'addEventListener' : 'removeEventListener']('scroll', this.onScroll, { passive: true });
            window[near ? 'addEventListener' : 'removeEventListener']('resize', this.onScroll);
            if (near) this.update();
          },
          { rootMargin: '20% 0px 20% 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        cancelAnimationFrame(this.frame);
      }

      /* -1 when the block is about to enter from below, 0 when centred, 1 when it has left at the top */
      update() {
        this.frame = null;
        const box = this.getBoundingClientRect();
        const travel = (window.innerHeight + box.height) / 2;
        const progress = Math.max(-1, Math.min(1, (window.innerHeight / 2 - (box.top + box.height / 2)) / travel));
        this.layers.forEach((layer) => {
          const shift = (parseFloat(layer.dataset.parallax) || 0) * progress;
          layer.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0)`;
        });
      }
    }
  );
}
